import React, { useEffect } from 'react';
import { useAnalytics } from '@/hooks/useAnalytics';

interface AnalyticsTrackerProps {
  children: React.ReactNode;
}

// Component to track page views and initialize analytics
export function AnalyticsTracker({ children }: AnalyticsTrackerProps) {
  const { trackPageView } = useAnalytics();

  useEffect(() => {
    // Track initial page view
    trackPageView();

    // Track page view changes for single-page apps
    const handleRouteChange = () => {
      trackPageView();
    };

    // Listen for route changes in React Router
    window.addEventListener('popstate', handleRouteChange);
    
    // Also track pushstate/replacestate for SPAs
    const originalPushState = history.pushState;
    const originalReplaceState = history.replaceState;
    
    history.pushState = function(...args) {
      originalPushState.apply(history, args);
      setTimeout(handleRouteChange, 0);
    };
    
    history.replaceState = function(...args) {
      originalReplaceState.apply(history, args);
      setTimeout(handleRouteChange, 0);
    };
    
    return () => {
      window.removeEventListener('popstate', handleRouteChange);
      history.pushState = originalPushState;
      history.replaceState = originalReplaceState;
    };
  }, [trackPageView]);

  return <>{children}</>;
}

// CTA Click Tracker Component
interface CTAButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  ctaName: string;
  location?: string;
  children: React.ReactNode;
}

export function CTAButton({ ctaName, location, children, onClick, ...props }: CTAButtonProps) {
  const { trackCTAClick } = useAnalytics();

  const handleClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    trackCTAClick(ctaName, location);
    if (onClick) {
      onClick(e);
    }
  };

  return (
    <button onClick={handleClick} {...props}>
      {children}
    </button>
  );
}